import { useEffect, useState, useMemo } from "react";
import { Alert, AlertDescription, Button } from "@tellescope/ui";
import { Dialog } from "@tellescope/design-system";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Download, Upload, Save, Loader2 } from "lucide-react";
import type { Session } from "@tellescope/sdk";
import type { PortalSchema } from "../../../shared/schema";
import { validatePortalSchema } from "../../../shared/schema/validation";
import { useSchemaExport } from "./useSchemaExport";
import { useSchemaImport } from "./useSchemaImport";
import type { ConfigurationTarget } from "../ConfigurationSelector";
import { useUpdateOrganization } from "../../hooks/useOrganizationMutations";
import { useUpdatePortalBranding } from "../../hooks/usePortalBrandingMutations";

interface SchemaActionsProps {
  schema: PortalSchema;
  savedSchema: PortalSchema | null;
  onImport: (json: unknown) => { success: boolean; errors?: string[] };
  onSaveSuccess: (schema: PortalSchema) => void;
  session: Session | null;
  configTarget: ConfigurationTarget;
  canEdit: boolean;
  canImport: boolean;
}

// Compare schemas ignoring updatedAt timestamp (which changes on every edit)
function schemasAreEqual(a: PortalSchema, b: PortalSchema): boolean {
  const { updatedAt: _aUpdated, ...aRest } = a;
  const { updatedAt: _bUpdated, ...bRest } = b;
  return JSON.stringify(aRest) === JSON.stringify(bRest);
}

export const SchemaActions: React.FC<SchemaActionsProps> = ({
  schema,
  savedSchema,
  onImport,
  onSaveSuccess,
  session,
  configTarget,
  canEdit,
  canImport,
}) => {
  const { t } = useTranslation();
  const { exportToFile } = useSchemaExport();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Mutation hooks for updating organization and portal branding
  const updateOrganization = useUpdateOrganization();
  const updatePortalBranding = useUpdatePortalBranding();

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!savedSchema) return false;
    return !schemasAreEqual(schema, savedSchema);
  }, [schema, savedSchema]);

  // Validate schema
  const validationResult = useMemo(() => validatePortalSchema(schema), [schema]);
  const isSchemaValid = validationResult.success;
  const validationErrors = validationResult.errors ?? [];

  const { openFilePicker, cleanup } = useSchemaImport((json) => {
    const result = onImport(json);
    if (result.success) {
      toast.success(t("Schema imported successfully"));
    } else {
      toast.error(t("Failed to import schema"), {
        description: result.errors?.join(", "),
      });
    }
    return result;
  });

  // Cleanup file input on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleExport = () => {
    exportToFile(schema);
    toast.success(t("Schema exported"));
  };

  const handleSaveConfiguration = async () => {
    if (!session) return;

    setIsSaving(true);
    setSaveDialogOpen(false);

    try {
      // 1. Create JSON blob from schema
      const schemaJson = JSON.stringify(schema, null, 2);
      const blob = new Blob([schemaJson], { type: "application/json" });
      const filename = `portal_configuration_${Date.now()}.json`;

      // 2. Upload file using SDK utility (handles prepare, S3 upload, and confirm)
      const fileRecord = await session.prepare_and_upload_file(
        {
          name: filename,
          size: blob.size,
          type: "application/json",
          publicRead: true, // Must be public for enduser portal to access
        },
        blob
      );

      // 3. Get public URL for the file
      const { downloadURL } = await session.api.files.file_download_URL({
        secureName: fileRecord.secureName,
      });

      // 4. Update the correct record based on configTarget using TanStack mutations
      if (configTarget.type === "default") {
        // Save to organization (default portal)
        await updateOrganization.mutateAsync({
          id: session.userInfo.businessId,
          portalV2SchemaURL: downloadURL,
        });
      } else {
        // Save to specific portal branding
        await updatePortalBranding.mutateAsync({
          id: configTarget.brandingId,
          portalV2SchemaURL: downloadURL,
        });
      }

      // Update the saved schema in the store so hasChanges becomes false
      onSaveSuccess(schema);
      toast.success(t("Configuration saved successfully"));
    } catch (error) {
      console.error("Failed to save configuration:", error);
      toast.error(t("Failed to save configuration"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={() => setSaveDialogOpen(true)}
          disabled={isSaving || !session || !hasChanges || !canEdit}
        >
          {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          {t("Save")}
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-1" />
          {t("Export")}
        </Button>
        <Button variant="outline" size="sm" onClick={openFilePicker} disabled={!canImport}>
          <Upload className="h-4 w-4 mr-1" />
          {t("Import")}
        </Button>
      </div>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>{t("Save Portal Configuration")}</Dialog.Title>
            <Dialog.Description>
              {isSchemaValid
                ? configTarget.type === "default"
                  ? t("Saving this configuration will update the default patient portal.")
                  : t("Saving this configuration will update the {{brandingTitle}} portal.", {
                      brandingTitle: configTarget.brandingTitle,
                    })
                : t("The current configuration has validation errors and cannot be saved.")}
            </Dialog.Description>
          </Dialog.Header>

          {/* Validation errors */}
          {!isSchemaValid && validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">{t("Schema Validation Errors")}</p>
                  <ul className="list-disc list-inside text-sm space-y-0.5">
                    {validationErrors.map((error, i) => (
                      <li key={i} className="break-all">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Dialog.Footer>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleSaveConfiguration} disabled={isSaving || !isSchemaValid}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {t("Save & Publish")}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
